import React, { useEffect, useMemo } from "react";
import MarkdownRenderer from "./MarkdownRenderer";
import { StreamingCitationProcessor, removeDocumentCitationsStreaming } from '../utils/streamingCitationProcessor';

interface StreamingTextProps {
  content: string;
  isStreaming: boolean;
  onComplete?: () => void;
  streamingProcessor?: StreamingCitationProcessor | null;
  citationProcessingEnabled?: boolean;
}

export default function StreamingText({ content, isStreaming, onComplete, streamingProcessor, citationProcessingEnabled = true }: StreamingTextProps) {
  // Process content based on streaming state
  const processedContent = useMemo(() => {
    if (isStreaming) {
      // During streaming: Remove citations for clean display
      return removeDocumentCitationsStreaming(content);
    } else {
      // When not streaming: Use content as-is (citations will be processed by parent)
      return content;
    }
  }, [content, isStreaming]);

  // Call onComplete when streaming stops
  useEffect(() => {
    if (!isStreaming && content.length > 0 && onComplete) {
      onComplete();
    }
  }, [isStreaming, content.length, onComplete]);

  // Always use markdown rendering to preserve formatting, but optimize with memoization
  const memoizedMarkdown = useMemo(() => {
    return (
      <div>
        <MarkdownRenderer>{processedContent}</MarkdownRenderer>
        {isStreaming && <span className="animate-pulse ml-1">|</span>}
      </div>
    );
  }, [processedContent, isStreaming]);

  return memoizedMarkdown;
}
